/**
 * Centralized employment type classification utility
 * This should be the ONLY place where employment type inference happens
 */

// Standardized employment type values - these are the only valid outputs
export const EMPLOYMENT_TYPES = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  LOCUM: 'Locum',
  CONTRACT: 'Contract', 
  NHS: 'NHS',
  PERMANENT: 'Permanent',
  TEMPORARY: 'Temporary'
};

/**
 * Classify employment type from text content
 * @param {string} text - Text to analyze (title, description, etc.)
 * @returns {string|null} - Standardized employment type or null
 */
export const classifyEmploymentType = (text) => {
  if (!text || typeof text !== 'string') {
    return null;
  }

  const normalizedText = text.toLowerCase().trim();
  
  // Contract patterns (check first as they're most specific)
  const contractPatterns = [
    /\b(\d+\s+month\s+contract)\b/i,
    /\bcontract\b/i,
    /\bfixed\s+term\b/i,
    /\bshort\s+term\b/i
  ];
  
  for (const pattern of contractPatterns) {
    if (pattern.test(normalizedText)) {
      return EMPLOYMENT_TYPES.CONTRACT;
    }
  }

  // Locum patterns
  const locumPatterns = [
    /\blocum\b/i,
    /\btemporary\b/i,
    /\btemp\b/i,
    /\bcasual\b/i,
    /\brelief\b/i,
    /\bsubstitute\b/i
  ];
  
  for (const pattern of locumPatterns) {
    if (pattern.test(normalizedText)) {
      return EMPLOYMENT_TYPES.LOCUM;
    }
  }

  // NHS patterns
  const nhsPatterns = [
    /\bnhs\b/i,
    /\bnational\s+health\b/i
  ];
  
  for (const pattern of nhsPatterns) {
    if (pattern.test(normalizedText)) {
      return EMPLOYMENT_TYPES.NHS;
    }
  }

  // Full-time patterns
  const fullTimePatterns = [
    /\bfull.?time\b/i,
    /\bfulltime\b/i,
    /\bft\b/i,
    /\bpermanent\b/i,
    /\bperm\b/i
  ];
  
  for (const pattern of fullTimePatterns) {
    if (pattern.test(normalizedText)) {
      return EMPLOYMENT_TYPES.FULL_TIME;
    }
  }

  // Part-time patterns
  const partTimePatterns = [
    /\bpart.?time\b/i,
    /\bparttime\b/i,
    /\bpt\b/i,
    /\bhalf\s+time\b/i
  ];
  
  for (const pattern of partTimePatterns) {
    if (pattern.test(normalizedText)) {
      return EMPLOYMENT_TYPES.PART_TIME;
    }
  }

  // Return null if no pattern matches
  return null;
};

/**
 * Extract employment type from multiple text sources
 * @param {Object} sources - Object with text sources
 * @returns {string|null} - Standardized employment type or null
 */
export const extractEmploymentType = (sources = {}) => {
  const { title, description, content } = sources;
  
  // Try each source in order of priority
  const textSources = [title, description, content].filter(Boolean);
  
  for (const text of textSources) {
    const type = classifyEmploymentType(text);
    if (type) {
      return type;
    }
  }
  
  return null;
};
